import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Button, Badge, Table, Modal, Input } from '@/components/ui';
import { patientService } from '@/services/patientService';
import { patientScoreService } from '@/services/patientScoreService';
import type { Patient, PatientSearchResult, CreatePatientRequest } from '@/types/patient';
import type { PatientScoreSummary, ScoreClassification } from '@/types/patientScore';
import styles from './PatientsPage.module.css';

const PAGE_SIZE = 10;

/**
 * Formats a CPF string (11 digits) as 000.000.000-00.
 */
function formatCpf(cpf: string): string {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

/**
 * Formats a phone string as (00) 00000-0000 or (00) 0000-0000.
 */
function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

/**
 * Validates a CPF string (digits only, 11 characters).
 * Checks digit count and rejects known-invalid patterns.
 */
function isValidCpf(cpf: string): boolean {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;
  return true;
}

/**
 * Admin page for listing and managing patients.
 *
 * Features:
 * - Search by name, CPF, or phone
 * - Paginated table listing
 * - Create and edit patients via modal
 * - View patient details
 * - Activate/deactivate patients
 */
export function PatientsPage() {
  const queryClient = useQueryClient();

  // Search and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const [viewingPatient, setViewingPatient] = useState<Patient | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formCpf, setFormCpf] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formBirthDate, setFormBirthDate] = useState('');
  const [formHealthPlanName, setFormHealthPlanName] = useState('');
  const [formHealthPlanNumber, setFormHealthPlanNumber] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Debounce search input
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  const {
    data: searchResult,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['patients', debouncedQuery, currentPage],
    queryFn: () => patientService.searchPatients(debouncedQuery, currentPage, PAGE_SIZE),
  });

  const patients = searchResult?.items ?? [];
  const totalPages = searchResult?.totalPages ?? 0;
  const totalCount = searchResult?.totalCount ?? 0;

  const patientIds = patients.map((p) => p.id);
  const { data: scoreSummaries } = useQuery({
    queryKey: ['patient-score-summaries', patientIds],
    queryFn: () => patientScoreService.getScoreSummaries(patientIds),
    enabled: patientIds.length > 0,
  });

  const scoreMap = new Map<string, PatientScoreSummary>(
    (scoreSummaries ?? []).map((s) => [s.patientId, s]),
  );

  const createMutation = useMutation({
    mutationFn: (data: CreatePatientRequest) => patientService.createPatient(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      closeCreateModal();
    },
    onError: () => {
      setFormError('Erro ao cadastrar paciente. Tente novamente.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreatePatientRequest }) =>
      patientService.updatePatient(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      closeEditModal();
    },
    onError: () => {
      setFormError('Erro ao atualizar paciente. Tente novamente.');
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (patient: Patient) =>
      patient.isActive
        ? patientService.deactivatePatient(patient.id)
        : patientService.activatePatient(patient.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
  });

  const resetForm = useCallback(() => {
    setFormName('');
    setFormEmail('');
    setFormCpf('');
    setFormPhone('');
    setFormBirthDate('');
    setFormHealthPlanName('');
    setFormHealthPlanNumber('');
    setFormNotes('');
    setFormError(null);
  }, []);

  const openCreateModal = useCallback(() => {
    resetForm();
    setIsCreateModalOpen(true);
  }, [resetForm]);

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    setFormError(null);
  }, []);

  const openEditModal = useCallback((patient: Patient) => {
    setFormName(patient.name);
    setFormEmail(patient.email ?? '');
    setFormCpf(patient.cpf);
    setFormPhone(patient.phone);
    setFormBirthDate(patient.birthDate ?? '');
    setFormHealthPlanName(patient.healthPlanName ?? '');
    setFormHealthPlanNumber(patient.healthPlanNumber ?? '');
    setFormNotes(patient.notes ?? '');
    setFormError(null);
    setEditingPatient(patient);
  }, []);

  const closeEditModal = useCallback(() => {
    setEditingPatient(null);
    setFormError(null);
  }, []);

  const openDetailModal = useCallback(
    (searchItem: PatientSearchResult) => {
      const cached = queryClient.getQueryData<Patient>(['patient', searchItem.id]);
      if (cached) {
        setViewingPatient(cached);
      } else {
        patientService.getPatient(searchItem.id).then((patient) => {
          setViewingPatient(patient);
          queryClient.setQueryData(['patient', patient.id], patient);
        });
      }
    },
    [queryClient],
  );

  const closeDetailModal = useCallback(() => {
    setViewingPatient(null);
  }, []);

  const buildPayload = useCallback((): CreatePatientRequest | null => {
    if (!formName.trim()) {
      setFormError('Nome é obrigatório.');
      return null;
    }
    if (!formCpf.trim()) {
      setFormError('CPF é obrigatório.');
      return null;
    }
    if (!isValidCpf(formCpf)) {
      setFormError('CPF inválido. Informe 11 dígitos.');
      return null;
    }
    if (!formPhone.trim()) {
      setFormError('Telefone é obrigatório.');
      return null;
    }
    return {
      name: formName.trim(),
      email: formEmail.trim() || null,
      cpf: formCpf.replace(/\D/g, ''),
      phone: formPhone.replace(/\D/g, ''),
      birthDate: formBirthDate || null,
      healthPlanName: formHealthPlanName.trim() || null,
      healthPlanNumber: formHealthPlanNumber.trim() || null,
      notes: formNotes.trim() || null,
    };
  }, [
    formName,
    formEmail,
    formCpf,
    formPhone,
    formBirthDate,
    formHealthPlanName,
    formHealthPlanNumber,
    formNotes,
  ]);

  const handleCreate = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      const payload = buildPayload();
      if (payload) {
        createMutation.mutate(payload);
      }
    },
    [buildPayload, createMutation],
  );

  const handleUpdate = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (!editingPatient) return;
      const payload = buildPayload();
      if (payload) {
        updateMutation.mutate({ id: editingPatient.id, data: payload });
      }
    },
    [buildPayload, editingPatient, updateMutation],
  );

  const handleToggleActive = useCallback(
    (event: React.MouseEvent, patient: Patient) => {
      event.stopPropagation();
      toggleActiveMutation.mutate(patient);
    },
    [toggleActiveMutation],
  );

  const handlePreviousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  }, [totalPages]);

  const columns = [
    {
      key: 'name',
      header: 'Paciente',
      render: (patient: PatientSearchResult) => (
        <div className={styles.patientInfo}>
          <span className={styles.patientName}>{patient.name}</span>
          <span className={styles.patientInfoSecondary}>{formatCpf(patient.cpf)}</span>
        </div>
      ),
    },
    {
      key: 'phone',
      header: 'Telefone',
      width: '160px',
      render: (patient: PatientSearchResult) => <span>{formatPhone(patient.phone)}</span>,
    },
    {
      key: 'score',
      header: 'Score',
      width: '120px',
      render: (patient: PatientSearchResult) => {
        const score = scoreMap.get(patient.id);
        if (!score) return <span style={{ color: 'var(--color-text-light)' }}>-</span>;
        const classConfig: Record<ScoreClassification, { label: string; color: string; bg: string }> = {
          GOLD: { label: 'Ouro', color: '#856404', bg: '#FFF3CD' },
          SILVER: { label: 'Prata', color: '#495057', bg: '#E9ECEF' },
          BRONZE: { label: 'Bronze', color: '#7B4F2A', bg: '#FDEBD0' },
          AT_RISK: { label: 'Risco', color: '#721C24', bg: '#F8D7DA' },
          INACTIVE: { label: 'Inativo', color: '#383D41', bg: '#D6D8DB' },
        };
        const cfg = classConfig[score.classification];
        const trendIcon = score.trend === 'UP'
          ? <TrendingUp size={12} style={{ color: '#28A745' }} />
          : score.trend === 'DOWN'
            ? <TrendingDown size={12} style={{ color: '#DC3545' }} />
            : <Minus size={12} style={{ color: '#FFC107' }} />;
        return (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 600,
            color: cfg.color,
            backgroundColor: cfg.bg,
          }}>
            {score.totalScore} {cfg.label} {trendIcon}
          </span>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '100px',
      render: (patient: PatientSearchResult) => (
        <Badge variant={patient.isActive ? 'success' : 'error'}>
          {patient.isActive ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      width: '120px',
      render: (patient: PatientSearchResult) => (
        <div className={styles.actions}>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              openDetailModal(patient);
            }}
          >
            Detalhes
          </Button>
        </div>
      ),
    },
  ];

  const formFields = (
    <>
      <div className={styles.formRow}>
        <Input
          label="Nome completo"
          value={formName}
          onChange={(e) => setFormName(e.target.value)}
          placeholder="Nome do paciente"
          required
          fullWidth
        />
      </div>
      <div className={styles.formRow}>
        <Input
          label="CPF"
          value={formCpf}
          onChange={(e) => setFormCpf(e.target.value)}
          placeholder="000.000.000-00"
          required
          fullWidth
        />
        <Input
          label="Telefone"
          value={formPhone}
          onChange={(e) => setFormPhone(e.target.value)}
          placeholder="(00) 00000-0000"
          required
          fullWidth
        />
      </div>
      <div className={styles.formRow}>
        <Input
          label="E-mail"
          type="email"
          value={formEmail}
          onChange={(e) => setFormEmail(e.target.value)}
          placeholder="email@exemplo.com"
          fullWidth
        />
        <Input
          label="Data de nascimento"
          type="date"
          value={formBirthDate}
          onChange={(e) => setFormBirthDate(e.target.value)}
          fullWidth
        />
      </div>
      <div className={styles.formRow}>
        <Input
          label="Convenio"
          value={formHealthPlanName}
          onChange={(e) => setFormHealthPlanName(e.target.value)}
          placeholder="Nome do convenio"
          fullWidth
        />
        <Input
          label="Número do convênio"
          value={formHealthPlanNumber}
          onChange={(e) => setFormHealthPlanNumber(e.target.value)}
          placeholder="Número da carteira"
          fullWidth
        />
      </div>
      <div className={styles.textareaWrapper}>
        <label htmlFor="patient-notes" className={styles.textareaLabel}>
          Observações
        </label>
        <textarea
          id="patient-notes"
          className={styles.textarea}
          value={formNotes}
          onChange={(e) => setFormNotes(e.target.value)}
          rows={3}
          placeholder="Alergias, condicoes especiais, etc."
        />
      </div>
      {formError && (
        <p className={styles.formError} role="alert">
          {formError}
        </p>
      )}
    </>
  );

  if (queryError) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState} role="alert">
          <p>Erro ao carregar pacientes. Tente novamente mais tarde.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Pacientes</h1>
          <p className={styles.subtitle}>Gerencie o cadastro de pacientes da clínica.</p>
        </div>
        <Button leftIcon={<Plus size={18} />} onClick={openCreateModal}>
          Novo Paciente
        </Button>
      </header>

      <div className={styles.searchBar}>
        <div className={styles.searchInputWrapper}>
          <Input
            label="Buscar paciente"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Nome, CPF ou telefone"
            leftIcon={<Search size={18} />}
            fullWidth
          />
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loadingState} role="status" aria-busy="true">
          <p>Carregando pacientes...</p>
        </div>
      ) : (
        <>
          <Table
            columns={columns}
            data={patients}
            keyExtractor={(patient) => patient.id}
            emptyMessage="Nenhum paciente encontrado."
          />

          {totalPages > 1 && (
            <nav className={styles.pagination} aria-label="Paginação de pacientes">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousPage}
                disabled={currentPage <= 1}
                leftIcon={<ChevronLeft size={16} />}
                aria-label="Página anterior"
              >
                Anterior
              </Button>
              <span className={styles.paginationInfo} aria-live="polite">
                Página {currentPage} de {totalPages} ({totalCount} pacientes)
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
                rightIcon={<ChevronRight size={16} />}
                aria-label="Próxima página"
              >
                Próxima
              </Button>
            </nav>
          )}
        </>
      )}

      {/* Create modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        title="Novo Paciente"
        size="lg"
      >
        <form onSubmit={handleCreate} className={styles.form} noValidate>
          {formFields}
          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={closeCreateModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={createMutation.isPending}>
              Cadastrar
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit modal */}
      <Modal
        isOpen={editingPatient !== null}
        onClose={closeEditModal}
        title="Editar Paciente"
        size="lg"
      >
        <form onSubmit={handleUpdate} className={styles.form} noValidate>
          {formFields}
          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={closeEditModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={updateMutation.isPending}>
              Salvar
            </Button>
          </div>
        </form>
      </Modal>

      {/* Detail modal */}
      <Modal
        isOpen={viewingPatient !== null}
        onClose={closeDetailModal}
        title="Detalhes do Paciente"
        size="lg"
      >
        {viewingPatient && (
          <div className={styles.detailSection}>
            <div className={styles.detailGrid}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Nome</span>
                <span className={styles.detailValue}>{viewingPatient.name}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>CPF</span>
                <span className={styles.detailValue}>{formatCpf(viewingPatient.cpf)}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Telefone</span>
                <span className={styles.detailValue}>{formatPhone(viewingPatient.phone)}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>E-mail</span>
                <span className={styles.detailValue}>{viewingPatient.email ?? 'Não informado'}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Data de nascimento</span>
                <span className={styles.detailValue}>
                  {viewingPatient.birthDate
                    ? new Date(viewingPatient.birthDate).toLocaleDateString('pt-BR')
                    : 'Não informada'}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Status</span>
                <Badge variant={viewingPatient.isActive ? 'success' : 'error'}>
                  {viewingPatient.isActive ? 'Ativo' : 'Inativo'}
                </Badge>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Convenio</span>
                <span className={styles.detailValue}>
                  {viewingPatient.healthPlanName ?? 'Nenhum'}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Número do convênio</span>
                <span className={styles.detailValue}>
                  {viewingPatient.healthPlanNumber ?? '-'}
                </span>
              </div>
            </div>
            {viewingPatient.notes && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Observações</span>
                <span className={styles.detailValue}>{viewingPatient.notes}</span>
              </div>
            )}
            <div className={styles.detailActions}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  closeDetailModal();
                  openEditModal(viewingPatient);
                }}
              >
                Editar
              </Button>
              <Button
                variant={viewingPatient.isActive ? 'danger' : 'primary'}
                size="sm"
                onClick={(e) => handleToggleActive(e, viewingPatient)}
                disabled={toggleActiveMutation.isPending}
              >
                {viewingPatient.isActive ? 'Desativar' : 'Ativar'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
